import { Icons } from '@/components/icons';
import { useCallback, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import useResizeObserver from 'use-resize-observer';
import { useGlobalFilterItems } from '../hooks/use-global-filter-items';
import type { GlobalFilterItem } from '../types';
import { filterScopeLabel } from '../utils';
import { GlobalFilterModal } from './global-filter-modal';

export function GlobalFilterSelector() {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [maxNameWidth, setMaxNameWidth] = useState(0);

  const calculateNameWidth = useCallback(() => {
    const totalWidth = btnRef.current?.offsetWidth ?? 0;
    const padding = 50;
    const nameWidth = Math.max(0, totalWidth - padding);
    setMaxNameWidth(nameWidth);
  }, []);

  const { ref: textRef } = useResizeObserver<HTMLDivElement>({
    onResize: calculateNameWidth,
  });

  const { data: cachedFilters, isLoading, isError } = useGlobalFilterItems();

  return (
    <>
      {/* Never seed the modal from a failed read — it saves by replacing the
          whole filter, so unresolved ids would be dropped on Spara. */}
      {open && !isLoading && !isError && (
        <GlobalFilterModal
          cachedFilters={cachedFilters ?? []}
          close={() => {
            setOpen(false);
          }}
        />
      )}
      <button
        ref={btnRef}
        type='button'
        onClick={() => setOpen(true)}
        className={cn(
          'flex w-full items-center justify-between rounded rounded-input border border-input bg-surface p-3 ring-offset-background transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
          {
            'border-primary bg-muted': !!cachedFilters?.length,
          }
        )}
      >
        <div className='flex flex-col text-start'>
          <FilterHeader currentFilters={cachedFilters ?? []} />
          <div
            style={{ maxWidth: maxNameWidth }}
            ref={textRef}
            className='mt-1 text-xs leading-none font-medium'
          >
            {isLoading && 'Laddar...'}
            {isError && 'Kunde inte läsa filtreringen'}
            {!isLoading && !isError && (
              <GlobalFilters currentFilters={cachedFilters ?? []} />
            )}
          </div>
        </div>

        <Icons.selectIndicator width={20} />
      </button>
    </>
  );
}

function GlobalFilters({
  currentFilters,
}: {
  currentFilters: GlobalFilterItem[];
}) {
  if (!currentFilters.length) {
    return 'Visar hela beståndet';
  }

  const str = `${currentFilters[0].displayString}`;

  const additional = currentFilters.length - 1;

  if (additional > 0) {
    return (
      <div className='flex flex-row items-center'>
        <div className='mr-1 truncate'>{str}</div>
        <Badge
          className='h-5 min-w-5 px-1 text-xs tabular-nums'
          variant='default'
        >
          +{additional}
        </Badge>
      </div>
    );
  }

  return str;
}

function FilterHeader({
  currentFilters,
}: {
  currentFilters: GlobalFilterItem[];
}) {
  return (
    <div className='text-xs leading-none'>
      {filterScopeLabel(currentFilters) || 'Filtrera systemet'}
    </div>
  );
}
